<script setup>
import { onMounted, reactive, ref } from "vue"
import { useApiRequest } from "../composables/useApiRequest"

const props = defineProps({
  endpoints: { type: Array, required: true },
  tokenStorageKey: { type: String, default: "dsa5_api_test_token" },
  labels: { type: Object, default: () => ({}) },
})

const token = ref("")
const formValues = reactive({})
const requests = ref([])

const label = (key, fallback) => props.labels[key] || fallback

function fieldKey(endpointIndex, fieldName) {
  return `${endpointIndex}-${fieldName}`
}

function initFormValues() {
  props.endpoints.forEach((endpoint, endpointIndex) => {
    endpoint.fields?.forEach((field) => {
      formValues[fieldKey(endpointIndex, field.name)] = field.defaultValue || ""
    })
  })
}

function getFields(endpointIndex, endpoint) {
  return (endpoint.fields || []).map((field) => ({
    name: field.name,
    value: formValues[fieldKey(endpointIndex, field.name)],
    pathParam: Boolean(field.pathParam),
  }))
}

async function submitEndpoint(endpointIndex, endpoint) {
  const request = requests.value[endpointIndex]
  await request.sendRequest({
    url: endpoint.url,
    method: endpoint.method,
    fields: getFields(endpointIndex, endpoint),
    token: token.value,
  })
}

onMounted(() => {
  token.value = localStorage.getItem(props.tokenStorageKey) || ""
  requests.value = props.endpoints.map(() => useApiRequest())
  initFormValues()
})

function saveToken() {
  localStorage.setItem(props.tokenStorageKey, token.value)
}
</script>

<template>
  <div class="api-tester-panel">
    <div style="margin-bottom: 2rem; padding-bottom: 1rem; border-bottom: 1px solid #333;">
      <label class="form-label">{{ label("tokenLabel", "API Token (JWT)") }}</label>
      <input
        v-model="token"
        type="password"
        class="form-control"
        :placeholder="label('tokenPlaceholder', 'Paste your JWT token here to use in all tests below')"
        @input="saveToken"
      >
      <small class="text-muted">{{ label("tokenHint", "Token is saved in your browser's local storage automatically.") }}</small>
    </div>

    <div class="api-endpoints">
      <div
        v-for="(endpoint, endpointIndex) in endpoints"
        :key="`${endpoint.method}-${endpoint.path}`"
        class="endpoint-card card mb-3"
      >
        <div class="card-header d-flex align-items-center">
          <span class="badge badge-success me-2">{{ endpoint.method }}</span>
          <code class="fs-5 me-2">{{ endpoint.path }}</code>
          <span v-if="endpoint.description" class="text-muted">- {{ endpoint.description }}</span>
        </div>

        <div class="card-body">
          <form @submit.prevent="submitEndpoint(endpointIndex, endpoint)">
            <div v-if="endpoint.fields?.length" class="row g-3 mb-3">
              <div
                v-for="field in endpoint.fields"
                :key="field.name"
                :class="field.colClass || 'col-md-2'"
              >
                <label class="form-label text-muted">
                  {{ field.label }}
                  <span v-if="field.required" class="text-danger">*</span>
                </label>
                <select
                  v-if="field.type === 'select'"
                  v-model="formValues[fieldKey(endpointIndex, field.name)]"
                  class="form-control form-control-sm"
                >
                  <option
                    v-for="option in field.options"
                    :key="option.value"
                    :value="option.value"
                  >
                    {{ option.label }}
                  </option>
                </select>
                <input
                  v-else
                  v-model="formValues[fieldKey(endpointIndex, field.name)]"
                  :type="field.type || 'text'"
                  :name="field.name"
                  :required="field.required"
                  class="form-control form-control-sm"
                  :placeholder="field.placeholder"
                >
              </div>
            </div>

            <div class="form-actions d-flex align-items-center">
              <button
                type="submit"
                class="btn btn-sm btn-primary me-3"
                :disabled="requests[endpointIndex]?.isLoading"
              >
                {{ label("testButton", "Test Endpoint") }}
              </button>
              <span
                v-if="requests[endpointIndex]?.statusText"
                :class="requests[endpointIndex]?.statusClass"
              >{{ requests[endpointIndex]?.statusText }}</span>
            </div>

            <pre
              v-if="requests[endpointIndex]?.showOutput"
              class="mt-3 p-2 rounded bg-dark text-light"
              style="max-height: 200px; overflow-y: auto; font-size: 0.85rem;"
            >{{ requests[endpointIndex]?.output }}</pre>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>
